import React from 'react';
import { UserInvitation, IdentityPasswordPolicy, IdentityPasswordComplexity } from 'appDtos';
import { connect} from 'react-redux';
import { Form, hasSubmitSucceeded } from 'redux-form';
import { RouteComponentProps } from 'react-router';
import { readPasswordPolicy, readUserInvitation } from './../../../common/api/products';
import {SlimHeader} from '../../../common/components/slim-header/SlimHeader';
import { passwordPolicyNames, passwordRequirementTitle } from '../../../common/components/set-password/set-password-requirements';
import {ConnectedSetPasswordForm} from '../../../common/components/set-password/SetPasswordForm';
import {SetPasswordChecklist} from '../../../common/components/set-password/SetPasswordChecklist';
import { LoadingIndicator, Sizes, Footer } from '@chr/navisphereonline-shared-components';
import './activation.scss';
import { ResponseStatus } from '@chr/online-shared-microsite-core/types';
import { loadAppConfig } from '@chr/online-shared-microsite-core';
import { IEBannerWarning } from '@chr/navisphereonline-shared-components';
import { OnlineTranslationContext } from '@chr/online-shared-ui-localizations';


interface LocationQuery {
  location: string;
}

export interface ActivationProps extends RouteComponentProps<LocationQuery> {
  form: Form,
  submitSucceeded: boolean,
}

interface ActivationState {
  userInvitation: UserInvitation;
  passwordPolicy?: IdentityPasswordPolicy;
  isLoading: boolean;
  isUrlValid: boolean;
  isInvitationExpired: boolean;
  isFormSubmitted: boolean;
  isUserActive: boolean;
  onlineUri: string;
  isIdentityActive: boolean;
}

export class Activation extends React.Component<ActivationProps, ActivationState> {
  state = {
    userInvitation: {} as UserInvitation,
    passwordPolicy: {} as IdentityPasswordPolicy,
    isLoading: true,
    isUrlValid: true,
    isInvitationExpired: false,
    isFormSubmitted: false,
    isUserActive: true,
    onlineUri: '',
    isIdentityActive: true
  };

  static contextType = OnlineTranslationContext;
  translate = (key: string, replaceVariable?: string[]) => this.context.translate ? this.context.translate(key, replaceVariable): key;

  async componentDidMount() {
    document.title = this.translate('Account Activation');
    this.getUserInvitation();
    const config = await loadAppConfig() as AppConfig;
    this.setState({onlineUri: config.online2Uri});
  }

  componentDidUpdate(prevProps: ActivationProps, prevState: ActivationState) {
    const { invitationId, isInvitationAccepted } = this.state.userInvitation;
    const { passwordPolicy } = this.state;

    if(this.props.submitSucceeded && !this.state.isFormSubmitted) {
      this.setState({isFormSubmitted: true});
    }

    if (invitationId && (!isInvitationAccepted && !passwordPolicy.settings)) {
      this.getPasswordPolicy();
    }
    if ((prevState.isLoading && passwordPolicy.settings) || (prevState.isLoading && isInvitationAccepted)) {
      this.setState({isLoading: false});
    }
  }

  getUserInvitation = async () => {
    const { search } = this.props.location;
    let { userInvitation, isUrlValid, isLoading, isInvitationExpired, isUserActive } = this.state;
    const params = new URLSearchParams(search);
    const invitationId = params.get('invitationId');
    const token = params.get('token');

    if (!invitationId || !token) {
      isUrlValid = false;
      isLoading = false;
    } else {
      const invitationIdInt = parseInt(invitationId as string);
      const response = await readUserInvitation(invitationIdInt, token);

      if (response.hasOwnProperty('errorCode')) {
        const error = response as ResponseStatus;
        if (error.message === 'Invitation expired') {
          isInvitationExpired = true;
          isLoading = false;
        } else if (error.message === `Invitation not found: ${invitationId}` ||
                   error.message === `Invalid token ${token}`) {
          isInvitationExpired = false;
          isLoading = false;
          isUrlValid = false;
        }
        else if(error.message === "User status is inactive") {
          isInvitationExpired = false;
          isLoading = false;
          isUrlValid = true;
          isUserActive = false;
        }
      } else {
        userInvitation = response as UserInvitation;
      }
    }

    this.setState({ userInvitation, isUrlValid, isLoading, isInvitationExpired, isUserActive });
  }

  getPasswordPolicy = async () => {
    const passwordPolicy = await readPasswordPolicy();
    const defaultPolicy = passwordPolicy.find((item: { name: string; }) => item.name === passwordPolicyNames.defaultPolicy);

    this.setState({
      passwordPolicy: defaultPolicy,
    });
	}

  getPasswordComplexity = () => {
    const {complexity} = this.state.passwordPolicy.settings.password;
    let isFirstNameValidationAdded = false;
    let isLastNameValidationAdded = false;

    if (!!complexity.excludeAttributes) {
      isFirstNameValidationAdded = complexity.excludeAttributes.indexOf(passwordRequirementTitle.firstName) !== -1 ? true : false;
      isLastNameValidationAdded = complexity.excludeAttributes.indexOf(passwordRequirementTitle.lastName) !== -1 ? true : false;
    };

    return {
      ...complexity,
      ...{validCharacters: true},
      ...{firstName: isFirstNameValidationAdded},
      ...{lastName: isLastNameValidationAdded},
    } as IdentityPasswordComplexity;
  };

  invitationAcceptedContainer = () => {
    return (
      <div data-testid="invitationAcceptedContainer">
        <div className="row">
          <div className="col-xs-12">
            <h1 className="d-flex flex-align-items-end">{this.translate("Account Activation")}</h1>
          </div>
        </div>
        <div className="row">
          <h2 className="col-xs-12 h4 text-muted">{this.translate("Welcome to Customer Platform, {0}", [this.state.userInvitation.firstName])}</h2>
          <p className="col-xs-12">{this.translate("Your Customer Platform account has been activated.")}</p>
          <p className="col-xs-12">{this.translate("Visit")} <a href={this.state.onlineUri}>{this.state.onlineUri.replace('https://', '')}</a> {this.translate("to log into Customer Platform applications")}</p>
        </div>
      </div>
    )
  };

  successfulSubmitContainer = () => {
    return (
      <div data-testid="successfulSubmitContainer">
        <div className="row">
            <div className="col-xs-12">
              <h1 className="d-flex flex-align-items-end">{this.translate("Account Activation")}</h1>
            </div>
          </div>
          <div className="row">
            <h2 className="col-xs-12 h4 text-muted">{this.translate("Welcome to Customer Platform, {0}", [this.state.userInvitation.firstName])}</h2>
            <p className="col-xs-12">{this.translate("Your password has been set for your account.")}</p>
            <p className="col-xs-12">{this.translate("Visit")} <a href={this.state.onlineUri}>{this.state.onlineUri.replace('https://', '')}</a> {this.translate("to log into Customer Platform applications")}</p>
          </div>
      </div>
    )
  };

  invitationExpiredContainer = (
    <div data-testid="invitationExpiredContainer">
      <div className="row">
        <div className="col-xs-12">
          <h1 className="d-flex flex-align-items-end">{this.translate("Account Activation")}</h1>
        </div>
      </div>
      <div className="row">
          <h2 className="col-xs-12 h4 text-muted">{this.translate("Invitation Expired")}</h2>
          <p className="col-xs-12">
            {this.translate("Oops... the invitation to activate your Customer Platform account has expired. Please ask your C.H. Robinson representative to send a new invitation.")}
          </p>
      </div>
    </div>
  );

  invalidLinkContainer = (
    <div data-testid="invalidLinkContainer">
      <div className="row">
        <div className="col-xs-12">
        <h1 className="d-flex flex-align-items-end">{this.translate("Account Activation")}</h1>
        </div>
      </div>
      <div className="row">
          <h2 className="col-xs-12 h4 text-muted">{this.translate("Invalid Activation Link")}</h2>
          <p className="col-xs-12">{this.translate("Oops... there is an issue with the link for your account activation.")}</p>
          <p className="col-xs-12">{this.translate("Please go back to your invitation email and click the account activation link again.")}</p>
          <p className="col-xs-12">{this.translate("If the problem continues, contact your C.H. Robinson representative for assistance.")}</p>
      </div>
    </div>
  );

  userStatusInactive = (
    <div data-testid="accountDeactivatedContainer">
      <div className="row">
        <div className="col-xs-12">
        <h1 className="d-flex flex-align-items-end">{this.translate("Account Activation")}</h1>
        </div>
      </div>
      <div className="row">
          <h2 className="col-xs-12 h4 text-muted">{this.translate("Account Deactivated")}</h2>
          <p className="col-xs-12">{this.translate("Oops... it looks like your access to C.H. Robinson customer applications was removed.")}</p>
          <p className="col-xs-12">{this.translate("Contact your C.H. Robinson representative for assistance.")}</p>
      </div>
    </div>
  );

  displayContent = () => {
    const { userInvitation, isLoading, isUrlValid, isInvitationExpired, isUserActive, isIdentityActive } = this.state;

    if (isLoading) {
      return <LoadingIndicator size={Sizes.Medium} data-testid="loading"/>;
    }

    if (!isUrlValid) {
      return this.invalidLinkContainer;
    }

    if (userInvitation.isInvitationAccepted) {
      return this.invitationAcceptedContainer();
    }

    if (isInvitationExpired) {
      return this.invitationExpiredContainer;
    }

    if (!isUserActive || !isIdentityActive) {
      return this.userStatusInactive
    }

    const { firstName, lastName, email } = this.state.userInvitation;

    if (this.state.isFormSubmitted) {
      return this.successfulSubmitContainer();
    }

    const { allowedSpecialCharacters } = this.state.passwordPolicy;
    const passwordRequirements = this.getPasswordComplexity();

    return (
      <>
        <div className="row">
          <div className="col-xs-12">
            <h1 className="d-flex flex-align-items-end">{this.translate("Account Activation")}</h1>
          </div>
        </div>
        <div className="row">
          <h2 className="col-xs-12 h4 text-muted">{this.translate("Welcome to Customer Platform, {0}", [firstName])}</h2>
          <p className="col-xs-12">{this.translate("To get started, set a password for your new account")}</p>
          <div className="col-xs-12 col-md-6 col-md-push-6 space-inner-top-md">
            <SetPasswordChecklist
              passwordRequirements={passwordRequirements}
              allowedSpecialCharacters={allowedSpecialCharacters}
              form={this.props.form}
              data-testid="setPasswordChecklist"
            />
          </div>
          <div className="col-xs-12 col-md-6 col-md-pull-6 space-inner-top-md border-md-right">
            <ConnectedSetPasswordForm
              email={email}
              firstName={firstName}
              lastName={lastName}
              passwordRequirements={passwordRequirements}
              location={this.props.location}
              data-testid="setPasswordForm"
              deactivatedIdentity={() => this.setState({ isIdentityActive: false })}
              />
          </div>
        </div>
      </>
    )
  };

  render() {
    return (
      <>
        <SlimHeader />
        <div className="container main">
          <IEBannerWarning />
          {this.displayContent()}
        </div>
        <Footer />
      </>
    );
  }
}

export const mapStateToProps = (state: AppState) => {
  return {
    form: state.form.setPasswordForm,
    submitSucceeded: hasSubmitSucceeded('setPasswordForm')(state),
  }
}

export default connect(mapStateToProps)(Activation);
